import {
  CheckOutlined,
  CloseCircleOutlined,
  EditOutlined,
} from '@ant-design/icons'
import type { ColumnDef } from '@tanstack/react-table'
import { Checkbox } from 'antd'
import type { ReactNode } from 'react'
import {
  type Control,
  Controller,
  type FieldErrors,
  type UseFormRegister,
} from 'react-hook-form'

import AtomFormInput from '@/components/Atoms/AtomFormInput/AtomFormInput'
import AtomSelect from '@/components/Atoms/AtomSelect/AtomSelect'
import HoverElement from '@/components/Atoms/HoverElement/HoverElement'
import type {
  ContractInterface,
  FormInputControlType,
  FormInputRegisterType,
  PaymentTypeInterface,
  SelectFormControlType,
} from '@/types'

import {
  type ContractEditFormType,
  getPaymentTypeOption,
} from './Contracts.config'
import cn from './Contracts.module.scss'
import type { ContractsSortField } from './types/contracts.types'

export const CONTRACTS_COLUMN_WIDTHS: Partial<Record<string, string>> = {
  actions: '90px',
  isActive: '140px',
  description: 'minmax(220px, 1.6fr)',
  number: 'minmax(160px, 1fr)',
  paymentType: 'minmax(170px, 1fr)',
  owner: 'minmax(150px, 1fr)',
  client: 'minmax(150px, 1fr)',
  createdAt: '150px',
  currency: '110px',
}

export type RenderContractsSortTitle = (
  field: ContractsSortField,
  title: string
) => ReactNode

type ContractsColumnsContext = {
  editingKey: string
  canEdit: boolean
  isOrganisationSelected: boolean
  paymentTypes: PaymentTypeInterface[]
  paymentTypesLoading: boolean
  control: Control<ContractEditFormType>
  register: UseFormRegister<ContractEditFormType>
  errors: FieldErrors<ContractEditFormType>
  renderSortTitle: RenderContractsSortTitle
  onEdit: (contract: ContractInterface, rowKey: string) => void
  onSave: (rowKey: string) => void
  onCancel: () => void
}

const renderActions = (
  contract: ContractInterface,
  context: ContractsColumnsContext
) => {
  if (!context.canEdit) return null

  if (contract.id !== context.editingKey)
    return (
      <div className={cn.actionsContainer}>
        <button
          type="button"
          className={cn.editOutlinedContainer}
          aria-label="Редактировать"
          onClick={() => context.onEdit(contract, contract.id)}
        >
          <EditOutlined />
        </button>
      </div>
    )

  return (
    <div className={cn.actionsContainer}>
      <button
        type="button"
        className={cn.saveIconContainer}
        aria-label="Сохранить"
        onClick={() => context.onSave(contract.id)}
      >
        <CheckOutlined />
      </button>
      <button
        type="button"
        className={cn.cancelIconContainer}
        aria-label="Отменить"
        onClick={context.onCancel}
      >
        <CloseCircleOutlined />
      </button>
    </div>
  )
}

const renderStatus = (
  contract: ContractInterface,
  context: ContractsColumnsContext
) => {
  if (contract.id === context.editingKey)
    return (
      <Controller
        name="isActive"
        control={context.control}
        render={({ field }) => (
          <Checkbox
            checked={field.value}
            onChange={(event) => field.onChange(event.target.checked)}
            aria-label="Действительный"
          />
        )}
      />
    )

  return (
    <span className={contract.isActive ? cn.statusActive : cn.statusInactive}>
      {contract.isActive ? 'Действительный' : 'Неактивный'}
    </span>
  )
}

const renderNumber = (
  contract: ContractInterface,
  context: ContractsColumnsContext
) => {
  if (contract.id !== context.editingKey) return contract.number

  return (
    <div className={cn.editField}>
      <AtomFormInput
        isOrganisationSelected={context.isOrganisationSelected}
        location="contractEdit"
        name="number"
        control={context.control as FormInputControlType}
        register={context.register as FormInputRegisterType}
        errors={context.errors}
        fullWidth
      />
    </div>
  )
}

const renderPaymentType = (
  contract: ContractInterface,
  context: ContractsColumnsContext
) => {
  if (contract.id !== context.editingKey)
    return contract.paymentTypeModel?.paymentType === 'CASH'
      ? 'Наличные'
      : 'Безналичный'

  return (
    <div className={cn.editField}>
      <HoverElement>
        <AtomSelect
          isOrganisationSelected={context.isOrganisationSelected}
          formName="paymentType"
          formControl={context.control as SelectFormControlType}
          formErrors={context.errors}
          options={context.paymentTypes.map(getPaymentTypeOption)}
          openByHover={false}
          loading={context.paymentTypesLoading}
          popupClassName={
            context.isOrganisationSelected
              ? cn.selectPopupSelected
              : cn.selectPopup
          }
          fullWidth
        />
      </HoverElement>
    </div>
  )
}

export const getContractsColumns = (
  context: ContractsColumnsContext
): ColumnDef<ContractInterface, unknown>[] => [
  {
    id: 'actions',
    header: 'Действия',
    cell: ({ row }) => renderActions(row.original, context),
  },
  {
    id: 'isActive',
    header: 'Статус',
    cell: ({ row }) => renderStatus(row.original, context),
  },
  {
    id: 'description',
    header: 'Наименование',
    cell: ({ row }) => row.original.description,
  },
  {
    id: 'number',
    header: () => context.renderSortTitle('number', 'Номер договора'),
    cell: ({ row }) => renderNumber(row.original, context),
  },
  {
    id: 'paymentType',
    header: 'Способ оплаты',
    cell: ({ row }) => renderPaymentType(row.original, context),
  },
  {
    id: 'owner',
    header: 'Владелец',
    cell: ({ row }) => row.original.ownerShortModel?.shortLegalName || '—',
  },
  {
    id: 'client',
    header: 'Клиент',
    cell: ({ row }) => row.original.clientShortModel?.description || '—',
  },
  {
    id: 'createdAt',
    header: () => context.renderSortTitle('createdAt', 'Дата создания'),
    cell: ({ row }) => new Date(row.original.creationDate).toLocaleDateString(),
  },
  {
    id: 'currency',
    header: 'Валюта',
    cell: ({ row }) => row.original.currencyModel?.description || '—',
  },
]
